import { useEffect } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';

import { getWorkspaceRolesFromIdToken, removeAuthQueryParams } from './helpers';

export function useReloadOnAuthError() {
  const { error, isLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (error && !isLoading && !isAuthenticated)
      window.location.replace(removeAuthQueryParams());
  }, [error, isLoading, isAuthenticated]);
}

export interface SignoutRedirectConfig {
  postLogoutRedirectUri?: string;
}

export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export function useAuthFlow() {
  const {
    user,
    isAuthenticated,
    isLoading,
    activeNavigator,
    error,
    signinRedirect,
    signinSilent,
    signoutRedirect,
  } = useAuth();

  const canAuthenticate = !(hasAuthParams() || isLoading || activeNavigator);
  const workspaceId = Object.keys(user?.profile?.workspaces || {})[0];
  const roles = getWorkspaceRolesFromIdToken(user?.profile, workspaceId);

  return {
    user: user && {
      id: user.profile.sub,
      firstName: user.profile.given_name,
      lastName: user.profile.family_name,
      email: user.profile.email,
    } satisfies User,
    workspaceId,
    error,
    roles,
    ensureAuthenticated: async () => {
      if (canAuthenticate && !isAuthenticated) {
        await signinRedirect();
      }
    },
    renewTokens: () => signinSilent(),
    accessToken: user?.access_token,
    signoutRedirect: ({ postLogoutRedirectUri }: SignoutRedirectConfig = {}) => signoutRedirect({
      post_logout_redirect_uri: postLogoutRedirectUri ?? window.location.origin,
    }),
  };
}
